import { CONTENT_COMPONENTS } from './components.mjs';
import { narrativeIssueBoxes, narrativeState } from './component-saju-narrative.mjs';
import { mountNarrativeReview } from './narrative-panel.mjs';

// One panel for every data component: the person it is calculated for (shared by all Saju
// components) and the fields the selected component declares (components.mjs `fields`).
export function mountComponentPanel({ getSection, run, upsert, remove, detach, canEdit, applySubject, personScope, selectElements, narrative: narrativeCommands }) {
  const el = (tag, attrs = {}, children = []) => {
    const node = document.createElement(tag);
    for (const [key, value] of Object.entries(attrs)) {
      if (key === 'text') node.textContent = value;
      else if (key.startsWith('on')) node.addEventListener(key.slice(2), value);
      else node.setAttribute(key, String(value));
    }
    node.append(...children); return node;
  };
  const field = (label, input) => el('label', { class: 'es-work-field' }, [el('span', { text: label }), input]);
  const option = (value, text) => el('option', { value, text });
  const input = (name, type, attrs = {}) => el('input', { type, 'data-testid': `component-${name}`, ...attrs });
  const pluginOf = type => CONTENT_COMPONENTS.find(plugin => plugin.type === type);
  const selected = el('select', { 'aria-label': '페이지의 컴포넌트', 'data-testid': 'component-select', onchange: () => load() });
  const type = el('select', { 'aria-label': '컴포넌트 종류', 'data-testid': 'component-type', onchange: () => { renderFields(); load(); } },
    CONTENT_COMPONENTS.map(plugin => Object.assign(option(plugin.type, plugin.label), plugin.insertable === false ? { hidden: true, disabled: true } : {})));
  const birthDate = input('birth-date', 'text', { placeholder: 'YYYY-MM-DD', pattern: '[0-9]{4}-[0-9]{2}-[0-9]{2}', maxlength: 10, required: '' });
  const birthTime = input('birth-time', 'time', { required: '' });
  const unknownTime = input('unknown-time', 'checkbox', { onchange: () => { birthTime.disabled = unknownTime.checked; } });
  const yajasi = input('yajasi', 'checkbox');
  const gender = el('select', { 'data-testid': 'component-gender' }, [option('male', '남성'), option('female', '여성')]);
  const calendar = el('select', { 'data-testid': 'component-calendar' }, [option('solar', '양력'), option('lunar', '음력')]);
  // The selected component's own fields, rebuilt when the type changes.
  const pluginFields = el('div', { class: 'es-component-fields', 'data-testid': 'component-fields' });
  let fieldInputs = new Map();
  function renderFields() {
    fieldInputs = new Map();
    pluginFields.replaceChildren(...pluginOf(type.value).fields.map(definition => {
      let control;
      if (definition.kind === 'select') control = el('select', { 'data-testid': `component-${definition.name}` }, Object.entries(definition.options).map(([value, label]) => option(value, label)));
      else if (definition.kind === 'color') control = input(definition.name, 'color');
      else control = input(definition.name, 'text', { maxlength: 100, required: '' });
      control.value = definition.default;
      fieldInputs.set(definition.name, control);
      return field(definition.label, control);
    }));
  }
  const status = el('p', { class: 'es-work-hint', 'data-testid': 'component-status', role: 'status' });
  const form = el('form', { class: 'es-component-form', onsubmit: event => { event.preventDefault(); void submit(false); } });
  const apply = el('button', { type: 'submit', class: 'es-work-primary', text: '계산해서 추가', 'data-testid': 'component-apply' });
  const action = (label, testid, callback) => el('button', { type: 'button', text: label, 'data-testid': testid, onclick: () => { void run(callback); } });
  const refreshButton = action('다시 계산', 'component-recalculate', () => submit(true, true));
  const detachButton = action('일반 콘텐츠로 분리', 'component-detach', async () => { await detach(selected.value); refresh(true); });
  const removeButton = action('컴포넌트 삭제', 'component-remove', async () => { await remove(selected.value); refresh(true); });
  // 명식 한 번에 바꾸기: the birth input above, for every component of this person on every page.
  const subjectButton = action('모든 쪽에 이 명식 적용', 'component-apply-subject', async () => {
    if (!form.reportValidity()) return;
    const id = selected.value, subject = props();
    await applySubject({ id, props: Object.fromEntries(['birthDate', 'birthTime', 'unknownTime', 'gender', 'isLunar', 'yajasi', 'year'].map(key => [key, subject[key]])) });
    refresh(false, id);
  });
  const scope = el('p', { class: 'es-work-hint', 'data-testid': 'component-person-scope' });
  // A book's narrative component: whose text it holds and what keeps it from printing (component-saju-narrative.mjs).
  const narrative = el('p', { class: 'es-work-hint', 'data-testid': 'component-narrative-state', role: 'status' });
  // Its text boxes that do not print as they are, selected on the canvas (lines box-<i>:line-<n>): nothing is changed.
  const showIssues = el('button', { type: 'button', text: '고칠 글상자 보기', 'data-testid': 'component-narrative-show', onclick: () => {
    const component = current();
    if (!component) return;
    const boxes = new Set(narrativeIssueBoxes(component.props, component.snapshot));
    selectElements(Object.entries(component.binding.elements).filter(([key]) => boxes.has(Number(/^box-(\d+):/.exec(key)?.[1]))).map(([, entry]) => entry.id));
  } });
  const props = () => ({
    birthDate: birthDate.value, birthTime: unknownTime.checked ? '12:00' : birthTime.value, unknownTime: unknownTime.checked,
    gender: gender.value, isLunar: calendar.value === 'lunar', yajasi: yajasi.checked, year: current()?.props.year ?? new Date().getFullYear(),
    ...Object.fromEntries([...fieldInputs].map(([name, control]) => [name, name === 'title' ? control.value.trim() : control.value])),
  });
  // Its text, slot by slot, to edit and review (narrative-panel.mjs).
  const reviewTab = mountNarrativeReview({ commands: narrativeCommands, run, refresh: () => refresh(false, selected.value) });
  // The selected component; its settings the form does not show (a book's narrative boxes and text) are kept.
  const current = () => getSection()?.components?.find(item => item.id === selected.value) ?? null;
  async function submit(recalculate, alreadyRunning = false) {
    if (!form.reportValidity()) return;
    const execute = async () => {
      const record = await upsert({ id: selected.value || undefined, type: type.value, props: { ...(current()?.props ?? {}), ...props() }, recalculate });
      refresh(false, record.id);
    };
    return alreadyRunning ? execute() : run(execute);
  }
  let sectionId = null;
  const selectionBySection = new Map();
  function load() {
    const component = getSection()?.components?.find(item => item.id === selected.value);
    if (component) {
      const p = component.props;
      if (type.value !== component.type) { type.value = component.type; renderFields(); }
      birthDate.value = p.birthDate; birthTime.value = p.birthTime; unknownTime.checked = p.unknownTime;
      yajasi.checked = p.yajasi === true; gender.value = p.gender; calendar.value = p.isLunar ? 'lunar' : 'solar';
      for (const [name, control] of fieldInputs) control.value = p[name];
    }
    type.disabled = Boolean(component);
    birthTime.disabled = unknownTime.checked;
    apply.textContent = component ? '설정 반영' : '계산해서 추가';
    for (const button of [refreshButton, detachButton, removeButton, subjectButton]) button.hidden = !component;
    const reach = component ? personScope(component.id) : null;
    scope.hidden = !reach;
    scope.textContent = reach ? `같은 명식의 컴포넌트 ${reach.components}개 · ${reach.pages.join('·')}쪽 — 명식을 고친 뒤 '모든 쪽에 이 명식 적용'을 누르면 함께 다시 계산합니다.` : '';
    narrative.hidden = component?.type !== 'saju.narrative';
    narrative.textContent = narrative.hidden ? '' : narrativeState(component.props, component.snapshot);
    showIssues.hidden = narrative.hidden || !narrativeIssueBoxes(component.props, component.snapshot).length || component.binding?.mode !== 'edgeless';
    reviewTab.show(component);
    const reason = canEdit();
    apply.disabled = Boolean(reason);
    status.textContent = reason || (component ? `마지막 계산 ${new Date(component.snapshot.calculatedAt).toLocaleString('ko-KR')} · 저장된 결과 사용 중` : pluginOf(type.value).description);
  }
  function refresh(reset = false, selection) {
    const section = getSection();
    const nextSectionId = section?.id ?? null;
    if (sectionId !== nextSectionId) {
      if (sectionId) selectionBySection.set(sectionId, selected.value);
      sectionId = nextSectionId;
      selection ??= selectionBySection.get(sectionId) ?? '';
    } else {
      selection ??= selected.value;
    }
    selected.replaceChildren(option('', '+ 새 컴포넌트'), ...(section?.components ?? []).map(item => option(item.id, `${pluginOf(item.type).label} · ${pluginOf(item.type).describe?.(item.props) ?? item.props.title}`)));
    selected.value = !reset && section?.components?.some(item => item.id === selection) ? selection : '';
    if (sectionId) selectionBySection.set(sectionId, selected.value);
    load();
  }
  renderFields();
  form.append(field('컴포넌트 종류', type), field('생년월일', birthDate), field('달력', calendar), field('태어난 시각', birthTime),
    field('시각 모름', unknownTime), field('야자시 적용 (23시 이후를 그날로)', yajasi), field('성별', gender), pluginFields,
    apply, refreshButton, detachButton, removeButton, subjectButton, scope, narrative, showIssues, reviewTab.element);
  const element = el('div', { class: 'es-work-panel', 'data-testid': 'studio-component-panel' }, [
    el('h3', { text: '데이터 컴포넌트' }), field('현재 페이지', selected), form, status,
    el('p', { class: 'es-work-hint', text: '사주 앱의 계산 결과와 입력값이 문서에 저장됩니다. 설정 반영은 직접 바꾼 배치·색상을 유지합니다. 일반 콘텐츠로 분리하면 계산 연결이 해제됩니다.' }),
  ]);
  return { element, refresh };
}
